import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { adminApi, apiError, formatDate } from "./api";

const STATUS_OPTIONS = [
  { value: "new", label: "New" },
  { value: "contacted", label: "Contacted" },
  { value: "closed", label: "Closed" },
];

const KIND_LABELS = { general: "General enquiry", property: "Property enquiry", valuation: "Valuation request" };

export default function AdminEnquiries() {
  const [data, setData] = useState({ results: [], total: 0 });
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    adminApi.get("/enquiries", { params: { status: status || undefined, per_page: 50 } })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (e, value) => {
    try {
      await adminApi.patch(`/enquiries/${e.id}`, { status: value });
      toast.success(`Marked as ${STATUS_OPTIONS.find((s) => s.value === value)?.label}.`);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const selectCls = "border border-ivory/20 bg-white/5 px-3 py-2 text-xs text-ivory focus:border-ivory focus:outline-none";

  return (
    <div data-testid="admin-enquiries">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl text-ivory sm:text-4xl">Enquiries</h1>
          <p className="mt-2 text-sm text-ivory/60">{data.total} received · contact details here are private — handle with care.</p>
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="enquiries-filter" className={selectCls}>
          <option value="" className="bg-charcoal">All statuses</option>
          {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value} className="bg-charcoal">{s.label}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><div className="h-7 w-7 animate-spin rounded-full border-2 border-ivory/20 border-t-ivory" /></div>
      ) : data.results.length === 0 ? (
        <div className="mt-8 border border-ivory/10 bg-[#242424] p-14 text-center" data-testid="enquiries-empty">
          <p className="font-serif text-2xl text-ivory">No enquiries yet</p>
          <p className="mt-2 text-sm text-ivory/50">New website enquiries will appear here automatically.</p>
        </div>
      ) : (
        <div className="mt-6 space-y-4" data-testid="enquiries-list">
          {data.results.map((e) => (
            <div key={e.id} className="border border-ivory/10 bg-[#242424] p-6" data-testid={`enquiry-${e.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="font-medium text-ivory">{e.name}</p>
                  <p className="mt-1 text-xs text-ivory/55">
                    <a className="underline underline-offset-2" href={`mailto:${e.email}`}>{e.email}</a>
                    {e.phone && <> · <a className="underline underline-offset-2" href={`tel:${e.phone}`}>{e.phone}</a></>}
                  </p>
                  <p className="mt-1 text-xs text-ivory/45">
                    {KIND_LABELS[e.kind] || e.kind}{e.listing_title ? ` · Re: ${e.listing_title}` : ""} · {formatDate(e.created_at)}
                  </p>
                </div>
                <select value={e.status || "new"} onChange={(ev) => updateStatus(e, ev.target.value)} data-testid={`enquiry-status-${e.id}`} className={selectCls}>
                  {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value} className="bg-charcoal">{s.label}</option>)}
                </select>
              </div>
              <p className="mt-4 border-l-2 border-ivory/15 pl-4 text-sm leading-relaxed text-ivory/75">{e.message}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
